import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Linkedin, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import useScrollAnimation from '../hooks/useScrollAnimation';

const ScrollReveal = ({ children, delay = 0, className = '' }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-1000 ${className} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const OurTeamPage = () => {
  const teamMembers = [
    {
      name: 'CA. K. Raghu',
      role: 'Managing Partner',
      bio: 'Over 30 years of experience in accounting and financial services. Leads strategic initiatives and provides expert guidance on complex financial matters.',
      image: '/images/ca-k-raghu.jpg',
      imagePosition: 'object-[center_15%]'
    },
    {
      name: 'CA Arjun KR',
      role: 'Senior Partner',
      bio: 'Expert in bookkeeping and audit services with extensive experience serving startups and SMBs.',
      image: '/images/ca-arjun-kr.jpg',
      imagePosition: 'object-[center_20%]'
    },
    {
      name: 'CA Vamsi S',
      role: 'Partner',
      bio: 'Experienced consultant specializing in financial analysis and business advisory services for growing enterprises.',
      image: '/images/vamsi-new.png',
      imagePosition: 'object-[center_15%]'
    },
    {
      name: 'CPA Shradha M',
      role: 'Senior Consultant',
      bio: 'Certified Public Accountant with deep expertise in statutory audit of listed entities and compliance.',
      image: '/images/shradha-mahesh.png',
      imagePosition: 'object-[center_20%]'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section - Dark Cinematic */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden bg-[#1a1a1a]">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&h=1080&fit=crop)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/90 to-[#1a1a1a]/60" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-24">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-[1px] bg-white/50"></div>
            <span className="text-white/50 text-sm tracking-widest uppercase">About Us</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Our <span className="text-[#8D6E63]">Team</span>
          </h1>
          <p className="text-xl text-white/70 max-w-2xl font-light">
            Meet the experts behind Trueledger. Our team brings decades of combined experience in accounting, taxation, and business consulting.
          </p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
            {teamMembers.map((member, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <TeamCard member={member} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal delay={0}>
              <ValueCard 
                title="Accuracy"
                description="We maintain the highest standards of precision in every financial record and report we deliver."
                icon="01"
              />
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <ValueCard 
                title="Accountability"
                description="We take full ownership of our work and stand behind every recommendation we make."
                icon="02"
              />
            </ScrollReveal>
            <ScrollReveal delay={300}>
              <ValueCard 
                title="Assurance"
                description="Our clients trust us to provide reliable, compliant, and professional financial services."
                icon="03"
              />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section - Simple */}
      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#2d2420] via-[#3E2723] to-[#2d2420]"></div>
        
        <ScrollReveal className="relative z-10 max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between">
          <div className="text-center md:text-left mb-6 md:mb-0">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Work With Us
            </h2>
            <p className="text-white/60 mt-1">Partner with a team that puts your success first</p>
          </div>
          <a href="mailto:hello@trueledger.com?subject=Work%20With%20Us%20Inquiry&body=Hi%20Trueledger%20Team,%0A%0AI%20am%20interested%20in%20working%20with%20your%20team.%0A%0AThank%20you.">
            <Button className="bg-white text-[#3E2723] hover:bg-[#8D6E63] hover:text-white px-8 py-5 text-base shadow-lg transition-all group">
              Get in Touch
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </ScrollReveal>
      </section>

      <Footer />
    </div>
  );
};

const TeamCard = ({ member }) => (
  <div className="bg-white border border-stone-200 rounded-lg overflow-hidden shadow-sm hover:shadow-xl hover:border-[#3E2723] transition-all duration-300 group h-full flex flex-col">
    {/* Image */}
    <div className="h-80 overflow-hidden bg-white flex-shrink-0 relative">
      {/* White gradient overlay for uniform look */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white/20 z-10 pointer-events-none"></div>
      <img 
        src={member.image} 
        alt={member.name}
        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${member.imagePosition || 'object-top'}`}
        style={{ filter: 'brightness(1.02) contrast(0.98)' }}
      />
    </div>
    
    {/* Content */}
    <div className="p-6 flex flex-col flex-grow">
      <h3 className="text-xl font-bold text-gray-900 mb-3">{member.name}</h3>
      <p className="text-gray-600 text-sm leading-relaxed flex-grow">{member.bio}</p>
    </div>
  </div>
);

const ValueCard = ({ title, description, icon }) => (
  <div className="group relative bg-white rounded-lg p-8 shadow-sm border border-stone-100 hover:shadow-xl hover:border-[#3E2723] transition-all duration-300 cursor-pointer overflow-hidden">
    {/* Hover Background */}
    <div className="absolute inset-0 bg-[#3E2723] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    
    {/* Content */}
    <div className="relative z-10">
      <span className="text-5xl font-bold text-stone-100 group-hover:text-white/20 transition-colors duration-300">{icon}</span>
      <h3 className="text-xl font-bold text-[#3E2723] group-hover:text-white mt-4 mb-3 transition-colors duration-300">{title}</h3>
      <p className="text-gray-600 group-hover:text-white/80 transition-colors duration-300">{description}</p>
    </div>
  </div>
);

export default OurTeamPage;
